// src/components/property/display/PropertyFinancials.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import './PropertyFinancials.css';
import './common.css';
import { currencyFormatter, percentFormatter } from '../../../utils/formatting';

const PropertyFinancials = ({ financialsData, calculations }) => {
  const { price, inspectionCost, closingCosts, additionalCosts, additionalCostsDescription, rent, hoa, propertyTax, insurance, managementFee, maintenance, utilities, vacancyRate, otherExpenses, otherExpensesDescription } = financialsData;
  const { t } = useTranslation();

  const { totalInitialCosts, monthlyManagementFeeAmount, monthlyVacancyLossAmount, totalMonthlyExpenses, totalYearlyExpenses, monthlyNetProfit, yearlyNetProfit, displayRoi } = calculations;

  return (
    <div className="financials-section">
      <h2 className="financials-heading">{t('propertyFinancials.title')}</h2>

      <h3 className="financials-subheading">{t('propertyFinancials.initialCosts')}</h3>
      <div className="framed-section financial-details-grid">
        <div className="grid-item">
          <i className="fas fa-money-bill-wave"></i> <span className="zillow-detail-label">{t('propertyFinancials.propertyPrice')}</span> <span className="zillow-detail-value">{currencyFormatter(price)}</span>
        </div>
        {inspectionCost !== '' && inspectionCost !== null && (
          <div className="grid-item">
            <i className="fas fa-search-dollar"></i> <span className="zillow-detail-label">{t('propertyFinancials.inspectionCost')}</span> <span className="zillow-detail-value">{currencyFormatter(inspectionCost)}</span>
          </div>
        )}
        {closingCosts !== '' && closingCosts !== null && (
          <div className="grid-item">
            <i className="fas fa-handshake"></i> <span className="zillow-detail-label">{t('propertyFinancials.closingCosts')}</span> <span className="zillow-detail-value">{currencyFormatter(closingCosts)}</span>
          </div>
        )}
        {additionalCosts !== '' && additionalCosts !== null && (
          <div className="grid-item">
            <i className="fas fa-plus-circle"></i> <span className="zillow-detail-label">{additionalCostsDescription || t('propertyFinancials.additionalCosts')}</span> <span className="zillow-detail-value">{currencyFormatter(additionalCosts)}</span>
          </div>
        )}
        <div className="grid-item total-costs-item">
          <i className="fas fa-calculator"></i> <span className="zillow-detail-label">{t('propertyFinancials.totalInitialCosts')}</span> <span className="zillow-detail-value bold-value">{currencyFormatter(totalInitialCosts)}</span>
        </div>
      </div>

      <div className="rent-section section-spacing">
        <div className="grid-item rent-item">
          <i className="fas fa-cash-register"></i> <span className="zillow-detail-label">{t('propertyFinancials.monthlyRent')}</span> <span className="zillow-detail-value zillow-rent-value bold-value" dangerouslySetInnerHTML={{ __html: rent || 'N/A' }} />
        </div>
      </div>

      <h3 className="financials-subheading">{t('propertyFinancials.recurringExpenses')}</h3>
      <div className="framed-section financial-details-grid">
        {hoa !== '' && hoa !== null && (
          <div className="grid-item">
            <i className="fas fa-coins"></i> <span className="zillow-detail-label">{t('propertyFinancials.hoa')}</span> <span className="zillow-detail-value">{hoa ? `${currencyFormatter(hoa)}${t('formatters.perMonth')}` : 'N/A'}</span>
          </div>
        )}
        {propertyTax !== '' && propertyTax !== null && (
          <div className="grid-item">
            <i className="fas fa-file-invoice-dollar"></i> <span className="zillow-detail-label">{t('propertyFinancials.propertyTax')}</span>
            <span className="zillow-detail-value">{propertyTax ? `${currencyFormatter(Number(propertyTax) / 12)}${t('formatters.perMonth')}` : 'N/A'}</span>
          </div>
        )}
        {insurance !== '' && insurance !== null && (
          <div className="grid-item">
            <i className="fas fa-shield-alt"></i> <span className="zillow-detail-label">{t('propertyFinancials.insurance')}</span>{' '}
            <span className="zillow-detail-value">{insurance ? `${currencyFormatter(Number(insurance) / 12)}${t('formatters.perMonth')}` : 'N/A'}</span>
          </div>
        )}
        {managementFee !== '' && managementFee !== null && (
          <div className="grid-item">
            <i className="fas fa-user-tie"></i> <span className="zillow-detail-label">{t('propertyFinancials.managementFee')}</span>
            <span className="zillow-detail-value" dangerouslySetInnerHTML={{ __html: `${percentFormatter(managementFee)} ${t('propertyFinancials.ofRent')} ${monthlyManagementFeeAmount ? `(${monthlyManagementFeeAmount})` : ''}` }} />
          </div>
        )}
        {maintenance !== '' && maintenance !== null && (
          <div className="grid-item">
            <i className="fas fa-wrench"></i> <span className="zillow-detail-label">{t('propertyFinancials.maintenance')}</span>{' '}
            <span className="zillow-detail-value">{maintenance ? `${currencyFormatter(Number(maintenance) / 12)}${t('formatters.perMonth')}` : 'N/A'}</span>
          </div>
        )}
        {utilities !== '' && utilities !== null && (
          <div className="grid-item">
            <i className="fas fa-lightbulb"></i> <span className="zillow-detail-label">{t('propertyFinancials.utilities')}</span>{' '}
            <span className="zillow-detail-value">{utilities ? `${currencyFormatter(utilities)}${t('formatters.perMonth')}` : 'N/A'}</span>
          </div>
        )}
        {vacancyRate !== '' && vacancyRate !== null && (
          <div className="grid-item">
            <i className="fas fa-house-damage"></i> <span className="zillow-detail-label">{t('propertyFinancials.vacancyRate')}</span>
            <span className="zillow-detail-value" dangerouslySetInnerHTML={{ __html: `${percentFormatter(vacancyRate)} ${monthlyVacancyLossAmount ? `(${monthlyVacancyLossAmount})` : ''}` }} />
          </div>
        )}
        {otherExpenses !== '' && otherExpenses !== null && (
          <div className="grid-item">
            <i className="fas fa-ellipsis-h"></i> <span className="zillow-detail-label">{otherExpensesDescription || t('propertyFinancials.otherExpenses')}</span>
            <span className="zillow-detail-value">{otherExpenses ? `${currencyFormatter(otherExpenses)}${t('formatters.perMonth')}` : 'N/A'}</span>
          </div>
        )}
        <div className="grid-item total-expenses-item">
          <i className="fas fa-file-invoice-dollar"></i> <span className="zillow-detail-label">{t('propertyFinancials.totalExpenses')}</span>
          <span className="zillow-detail-value bold-value">
            <span dangerouslySetInnerHTML={{ __html: totalMonthlyExpenses }} />
            <span className="yearly-total-display" dangerouslySetInnerHTML={{ __html: totalYearlyExpenses }} />
          </span>
        </div>
      </div>

      <div className="net-profit-section section-spacing">
        <div className="grid-item net-profit-item">
          <i className="fas fa-chart-line"></i>
          <span className="zillow-detail-label">{t('propertyFinancials.netProfit')}:</span>
          <span className="zillow-detail-value bold-value">
            <span dangerouslySetInnerHTML={{ __html: monthlyNetProfit }} />
            <span className="yearly-total-display" dangerouslySetInnerHTML={{ __html: yearlyNetProfit }} />
            {displayRoi && <span className="roi-in-profit" dangerouslySetInnerHTML={{ __html: `(${t('propertyFinancials.roi')}: ${displayRoi})` }} />}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PropertyFinancials;
